import { memo, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Navigate } from 'react-router-dom';

import type IAllowedSystemEnum from '~/enums/allowedSystem.enum';
import { useFeatureFlagsInit } from '~/hooks/featureFlags/useFeatureFlagsInit';
import useAuthStore from '~/stores/auth/useAuthStore';
import { useTokenStore } from '~/stores/token/useTokenStore';

type IProtectedRouteProps = {
  children: React.ReactNode;
  allowedSystem?: IAllowedSystemEnum;
};

const isAuthenticated = async (): Promise<boolean> => {
  try {
    await useAuthStore.getState().authTokenSlice.ensureValidToken(undefined);

    return Boolean(useTokenStore.getAccess()) && Boolean(useTokenStore.getRefresh());
  } catch {
    return false;
  }
};

function ProtectedRoute({ children, allowedSystem }: IProtectedRouteProps): JSX.Element {
  const [isAuth, setIsAuth] = useState<boolean | null>(null);
  const allowedSystems = useAuthStore(state => state.tokenClaim.allowedSystems);

  useFeatureFlagsInit();

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const ok = await isAuthenticated();
        if (alive) setIsAuth(ok);
      } catch {
        if (alive) setIsAuth(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  if (isAuth === null) return <div>Carregando...</div>;
  if (!isAuth) return <Navigate to="/" replace />;

  if (allowedSystem !== undefined && !allowedSystems.includes(String(allowedSystem))) {
    return <Navigate to="/apps" replace />;
  }

  return children as JSX.Element;
}

export default memo(ProtectedRoute);
